import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { usePortalBranding } from '@/hooks/usePortalMe'

import { PortalBrandTheme } from './PortalBrandTheme'

/**
 * FE-7 / FE-13 — **l'état d'erreur PLEINE PAGE d'un espace** (DESIGN_SYSTEM §15.7).
 *
 * Deux écrans en avaient besoin et n'en avaient aucun : la page de choix rendait
 * `null` à vie sur une panne (page BLANCHE), et la coque traitait un 5xx de
 * `/api/portal/me` comme « pas de session » — elle renvoyait vers la connexion,
 * la connexion renvoyait dans la coque, et la boucle ne disait rien.
 *
 * ⚠️ **Dans la marque de l'organisme**, pas dans celle d'Argalis : `/me` a
 * échoué, donc l'organisation du contexte est inconnue — mais l'habillage
 * PUBLIC du sous-domaine (`/api/portal/branding`) ne dépend d'aucune session.
 * C'est lui qui peint cet écran, comme il peint la page de connexion.
 *
 * `code` est le statut HTTP (ou `NET` pour une coupure) : c'est ce que la
 * personne pourra citer à son organisme, et `portal.context.error` l'attend.
 */
export function PortalErreurPleinePage({
    code,
    onReessayer,
    onSeDeconnecter,
}: {
    code: string
    onReessayer: () => void
    onSeDeconnecter?: () => void
}) {
    const { t } = useTranslation()
    const { data: habillage } = usePortalBranding()

    return (
        <div className="flex min-h-[100svh] flex-col items-center justify-center bg-background px-4 pt-[calc(1.5rem+env(safe-area-inset-top))] pb-[calc(1.5rem+env(safe-area-inset-bottom))] text-center">
            <PortalBrandTheme
                name={habillage?.name}
                primaryColor={habillage?.primary_color}
                secondaryColor={habillage?.secondary_color}
                faviconUrl={habillage?.favicon_url}
            />
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
                <AlertTriangle className="h-8 w-8 text-destructive" aria-hidden />
            </div>
            <h1 className="mt-4 text-2xl font-bold tracking-tight">{t('common.error')}</h1>
            <p role="alert" className="mt-2 max-w-md text-sm text-muted-foreground">
                {t('portal.context.error', { code })}
            </p>
            <div className="mt-6 flex w-full max-w-xs flex-col gap-2">
                <Button className="h-11" onClick={onReessayer}>
                    {t('portal.context.retry')}
                </Button>
                {onSeDeconnecter && (
                    <Button variant="outline" className="h-11" onClick={onSeDeconnecter}>
                        {t('portal.context.signOut')}
                    </Button>
                )}
            </div>
        </div>
    )
}
